import { useEffect, useState, useCallback } from "react";
import { Button } from "primereact/button";
import { BoxTable } from "./components/BoxTable";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import { Box } from "../../../../interface/interface";
import { useDispatch } from "react-redux";
import { assignCashRegisterToLocation, getCashRegistersForAdministration, unassignCashRegisterFromLocation } from "../../../../services/LocationCashController";
import { CashRegisterDTO } from "../../../../dto/locationcash/LocationCashDTO";
import useResizeListener from "../../../../hooks/useResizeListener";
import LocationEstablishmentFilter from "../components/LocationEstablishmentFilter";
import useEstablishmentFilterSelection from "../../hooks/useEstablishmentFilterSelection";
import CashRegisterAssignmentModal from "./components/CashRegisterAssignmentModal";
import { removeHospitalLocation } from "../../../../servicesHooks/Location/useLocationConfigService";

export default function BoxView() {
    const isMobile = useResizeListener(768);
    const { establishments, orderedEstablishments, setEstablishments, setBoxesToEstablishments, setCallersToEstablishments, setTotemList } = useLocationConfigContext();
    const dispatch = useDispatch();

    const [elimBox, setElimBox] = useState<Box>();
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const { selectedEstablishmentIds, setSelectedEstablishmentIds } = useEstablishmentFilterSelection(isMobile);

    const [cashConfigBox, setCashConfigBox] = useState<Box | null>(null);
    const [cashConfigVisible, setCashConfigVisible] = useState(false);
    const [cashRegisterMap, setCashRegisterMap] = useState<Map<number, CashRegisterDTO>>(new Map());
    const [availableCashRegisters, setAvailableCashRegisters] = useState<CashRegisterDTO[]>([]);

    const loadCashRegisterMap = useCallback(async () => {
        const allRegisters = await getCashRegistersForAdministration();
        setAvailableCashRegisters(allRegisters);
        const newMap = new Map<number, CashRegisterDTO>();
        allRegisters.forEach(register => {
            if (register.hospitalLocationId != null) {
                newMap.set(register.hospitalLocationId, register);
            }
        });
        setCashRegisterMap(newMap);
    }, [establishments]);

    useEffect(() => {
        loadCashRegisterMap();
    }, [loadCashRegisterMap]);

    const handleConfigureCash = (box: Box) => {
        setCashConfigBox(box);
        setCashConfigVisible(true);
    };

    const handleCashSaved = async (cashRegister: CashRegisterDTO | null) => {
        if (!cashConfigBox) return;
        const hospitalLocationId = cashConfigBox.hospitallocation;
        if (cashRegister) {
            await assignCashRegisterToLocation(cashRegister.locationCashRegisterId, hospitalLocationId);
            setCashRegisterMap(prev => new Map(prev).set(hospitalLocationId, { ...cashRegister, hospitalLocationId }));
        } else {
            const current = cashRegisterMap.get(hospitalLocationId);
            if (current) await unassignCashRegisterFromLocation(current.locationCashRegisterId);
            setCashRegisterMap(prev => {
                const next = new Map(prev);
                next.delete(hospitalLocationId);
                return next;
            });
        }
        const allRegisters = await getCashRegistersForAdministration();
        setAvailableCashRegisters(allRegisters);
    };

    const updateBoxes = (index: number, newBoxes: Box[]) => { //Actualiza boxes del establecimiento
        const updatedEstablishments = [...establishments];
        updatedEstablishments[index].boxes = newBoxes;
        setEstablishments(updatedEstablishments);
        setCallersToEstablishments(updatedEstablishments); //Para que si están asociados, se actualicen los datos del puesto en esos lugares
        setTotemList();
    };

    const handleOpenRemoveModal = (box: Box) => {
        setElimBox(box);
        setOpenRemoveModal(true);
    };

    const handleDelete = (box: Box) => { //Elimina el box, controla asociación a totem/llamador
        removeHospitalLocation(box.hospitallocation)
            .then(() => {
                dispatch(setNotification({ title: "Eliminación exitosa", desc: "Se ha eliminado el puesto", icon: "pi-check-circle", typeOfNotification: ETypeOfNotification.INFO }));
                setBoxesToEstablishments();

                setElimBox(null);
                setOpenRemoveModal(false);
            }).catch((err) => {
                console.error(err);
                if (err.response?.status === 409) {
                    if (err.response?.data === "ASSOCIATED_CALLER") {
                        dispatch(setNotification({ title: "No se puede eliminar", desc: "Debe desasociar del llamador antes de eliminar.", icon: "pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
                    }
                    if (err.response?.data === "ASSOCIATED_TOTEM") {
                        dispatch(setNotification({ title: "No se puede eliminar", desc: "Debe desasociar del tótem antes de eliminar.", icon: "pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
                    }
                } else {
                    dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo eliminar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
                }
            });
    };

    const establishmentsToRender = isMobile
        ? orderedEstablishments.filter(est => selectedEstablishmentIds.includes(est.buildingid))
        : orderedEstablishments;

    return (
        <div>
            <h1 className="title2">Puesto de atención</h1>
            <div className="flex flex-row justify-content-between emr-border-bottom pb-2">
                <p className="grey-color">Las puestos se asocian a los sectores de establecimientos creados.</p>
                {<></>/*<Button icon='pi pi-search' onClick={() => {}} className="btnSearch"><p className="btnSearch-text ml-2">Buscar este dispositivo</p></Button>*/}
            </div>

            {isMobile && (
                <LocationEstablishmentFilter
                    establishments={orderedEstablishments}
                    selectedEstablishmentIds={selectedEstablishmentIds}
                    onChange={setSelectedEstablishmentIds}
                />
            )}


            {establishmentsToRender.map((est) => {
                const realIndex = establishments.findIndex(x => x.buildingid === est.buildingid);
                return (
                    <div key={est.buildingid} className="my-5">
                        {!isMobile && (
                            <div className="flex flex-row gap-4">
                                <h2 className="title3 mb-2">{est.buildingname}</h2>
                                <p className="grey-color mt-1">{est.buildingaddress}</p>
                            </div>
                        )}
                        <BoxTable
                            boxes={est.boxes}
                            onChange={(newBoxes) => updateBoxes(realIndex, newBoxes)}
                            onDelete={(box) => handleOpenRemoveModal(box)}
                            sectors={est.sectors}
                            estId={est.buildingid}
                            mobileTitle={est.buildingname}
                            mobileSubtitle={est.buildingaddress}
                            onConfigureCash={handleConfigureCash}
                            cashRegisterMap={cashRegisterMap}
                        />
                    </div>
                );
            })}

            <Modal className="location-mobile-editor-modal" visible={openRemoveModal} setVisible={setOpenRemoveModal} header={"Estás por eliminar el puesto"} headerStyle={{ fontSize: "12px", textAlign: "center" }} width="w-medium" closable={false}>
                <div>
                    <p className="grey-color">No podrás eliminar puestos que estén asociados a un llamador o a un tótem.</p>
                    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <Button id="deletebuttonid" label="Si, eliminar" onClick={() => handleDelete(elimBox)} className="w-full buttonMain" />
                        <Button id="canceldeletebuttonid" label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                    </div>
                </div>
            </Modal>

            <CashRegisterAssignmentModal
                visible={cashConfigVisible}
                setVisible={setCashConfigVisible}
                box={cashConfigBox}
                currentCashRegister={cashConfigBox ? cashRegisterMap.get(cashConfigBox.hospitallocation) ?? null : null}
                availableCashRegisters={availableCashRegisters}
                onSaved={handleCashSaved}
                notify={(notification) => dispatch(setNotification(notification))}
            />
        </div>
    );
}
